/* OpeningBalancesWizard — set NHF's day-one Balance Sheet.
 * User enters dollar balances for Assets (1xxx) and Liabilities (2xxx),
 * then splits the residual Net Assets across 3100/3200/3300. Server
 * validates Assets − Liabilities == Net Assets allocated, then writes
 * a single self-balancing journal entry tagged source_type='opening_balance'.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { X, Save, Loader2, AlertCircle, CheckCircle, Coins } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const todayIso = () => new Date().toISOString().slice(0, 10);
const NET_ASSET_FIELDS = [
  { code: '3100', label: 'Without Donor Restriction',
    hint: 'Funds the board may use for any mission purpose.', field: 'net_assets_unrestricted' },
  { code: '3200', label: 'With Donor Restriction',
    hint: 'Funds restricted by donor (purpose- or time-restricted).', field: 'net_assets_restricted' },
  { code: '3300', label: 'Board-Designated',
    hint: 'Funds the board has set aside for a specific future use.', field: 'net_assets_board_designated' },
];

const OpeningBalancesWizard = ({ open, onClose, onSaved }) => {
  const [entryDate, setEntryDate] = useState(todayIso());
  const [memo, setMemo] = useState('');
  const [assetAccounts, setAssetAccounts] = useState([]);
  const [liabilityAccounts, setLiabilityAccounts] = useState([]);
  const [values, setValues] = useState({});                // {code: number}
  const [naSplit, setNaSplit] = useState({
    net_assets_unrestricted: 0,
    net_assets_restricted: 0,
    net_assets_board_designated: 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setError(''); setMemo('');
    setEntryDate(todayIso());
    setValues({});
    setNaSplit({
      net_assets_unrestricted: 0,
      net_assets_restricted: 0,
      net_assets_board_designated: 0,
    });
    axios.get(`${BACKEND_URL}/api/admin/chart-of-accounts`, { withCredentials: true })
      .then((r) => {
        const all = r.data?.accounts || [];
        setAssetAccounts(all.filter((a) => a.type === 'asset'));
        setLiabilityAccounts(all.filter((a) => a.type === 'liability'));
      })
      .catch((e) => setError(e.response?.data?.detail || 'Could not load chart of accounts.'))
      .finally(() => setLoading(false));
  }, [open]);

  const num = (v) => Number.parseFloat(v) || 0;
  const assetTotal = useMemo(
    () => assetAccounts.reduce((s, a) => s + num(values[a.code]), 0),
    [assetAccounts, values],
  );
  const liabilityTotal = useMemo(
    () => liabilityAccounts.reduce((s, a) => s + num(values[a.code]), 0),
    [liabilityAccounts, values],
  );
  const expectedNa = useMemo(() => Math.round((assetTotal - liabilityTotal) * 100) / 100,
    [assetTotal, liabilityTotal]);
  const naAllocated = useMemo(
    () => NET_ASSET_FIELDS.reduce((s, f) => s + num(naSplit[f.field]), 0),
    [naSplit],
  );
  const naDiff = Math.round((expectedNa - naAllocated) * 100) / 100;
  const isBalanced = Math.abs(naDiff) < 0.01 && (assetTotal > 0 || liabilityTotal > 0 || naAllocated > 0);

  const autofillUnrestricted = () => {
    setNaSplit((s) => ({
      ...s,
      net_assets_unrestricted: Math.max(0, num(s.net_assets_unrestricted) + naDiff),
    }));
  };

  const save = async () => {
    setError('');
    if (!entryDate) return setError('Pick an opening balance date.');
    if (!isBalanced) {
      return setError('Net assets must equal assets − liabilities. Adjust your split before saving.');
    }
    setSaving(true);
    try {
      const payload = {
        entry_date: entryDate,
        memo: memo.trim() || `Opening balances as of ${entryDate}`,
        asset_lines: assetAccounts
          .filter((a) => num(values[a.code]) > 0)
          .map((a) => ({ account_code: a.code, amount: num(values[a.code]) })),
        liability_lines: liabilityAccounts
          .filter((a) => num(values[a.code]) > 0)
          .map((a) => ({ account_code: a.code, amount: num(values[a.code]) })),
        net_assets_unrestricted: num(naSplit.net_assets_unrestricted),
        net_assets_restricted: num(naSplit.net_assets_restricted),
        net_assets_board_designated: num(naSplit.net_assets_board_designated),
      };
      const r = await axios.post(`${BACKEND_URL}/api/admin/journal-entries/opening-balances`,
        payload, { withCredentials: true });
      onSaved?.(r.data);
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save opening balances.');
    }
    setSaving(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
      data-testid="opening-balances-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-[#E5E7EB] sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-[#BA1826]" />
            <h2 className="font-heading text-lg font-bold text-[#0A0A0A]">Set NHF&apos;s opening balances</h2>
          </div>
          <button onClick={onClose} className="text-[#4B5563] hover:text-[#0A0A0A]" data-testid="opening-balances-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <p className="text-xs text-[#4B5563] bg-[#FFF8F8] border border-[#BA1826]/30 rounded p-2">
            Enter NHF&apos;s real balances as of your starting date — pull these from your latest bank
            statement, outstanding pledges, and merchandise inventory count. The wizard auto-balances
            the books so the equation <span className="font-mono font-bold">Assets = Liabilities + Net Assets</span> holds
            from day one. Anything you don&apos;t know yet, leave at $0 and update later with a regular journal entry.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#4B5563] mb-1">Opening date *</label>
              <input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)}
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
                data-testid="opening-balances-date" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#4B5563] mb-1">Memo (optional)</label>
              <input value={memo} onChange={(e) => setMemo(e.target.value)}
                placeholder="Pulled from Feb 1 2026 bank statement…"
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm" />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>
          ) : (
            <>
              {/* Assets */}
              <BalanceList
                title="Assets (what NHF owns)"
                color="blue"
                accounts={assetAccounts}
                values={values}
                onChange={(code, v) => setValues((s) => ({ ...s, [code]: v }))}
                total={assetTotal}
                testidPrefix="asset"
              />

              {/* Liabilities */}
              <BalanceList
                title="Liabilities (what NHF owes)"
                color="purple"
                accounts={liabilityAccounts}
                values={values}
                onChange={(code, v) => setValues((s) => ({ ...s, [code]: v }))}
                total={liabilityTotal}
                testidPrefix="liability"
              />

              {/* Net assets split */}
              <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid="na-split-card">
                <div className="bg-[#FAFBFC] px-3 py-2 border-b border-[#E5E7EB] flex items-center justify-between flex-wrap gap-2">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#4B5563]">
                    Net Assets (Assets − Liabilities = ${expectedNa.toFixed(2)})
                  </p>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded ${
                      isBalanced ? 'bg-green-50 text-green-800' :
                      naAllocated === 0 ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-800'
                    }`} data-testid="na-balance-status">
                      {isBalanced ? 'Balanced' : naAllocated === 0 ? `Allocate $${expectedNa.toFixed(2)}` : `Off by $${naDiff.toFixed(2)}`}
                    </span>
                    {!isBalanced && Math.abs(naDiff) > 0.01 && expectedNa > 0 && (
                      <button onClick={autofillUnrestricted}
                        className="text-[10px] px-2 py-0.5 border border-[#BA1826]/40 text-[#BA1826] rounded font-semibold"
                        data-testid="na-autofill-btn">
                        Auto-fill 3100
                      </button>
                    )}
                  </div>
                </div>
                <div className="divide-y divide-[#E5E7EB]">
                  {NET_ASSET_FIELDS.map((f) => (
                    <div key={f.code} className="px-3 py-2 flex items-center gap-3">
                      <span className="font-mono font-bold text-xs w-12">{f.code}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-[#0A0A0A]">{f.label}</p>
                        <p className="text-[10px] text-[#9CA3AF] truncate">{f.hint}</p>
                      </div>
                      <input type="number" step="0.01" value={naSplit[f.field] || ''}
                        onChange={(e) => setNaSplit((s) => ({ ...s, [f.field]: e.target.value }))}
                        placeholder="0.00"
                        className="w-28 px-2 py-1 border border-[#E5E7EB] rounded text-xs text-right font-mono"
                        data-testid={`na-input-${f.code}`} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Live equation */}
              <div className="bg-[#0A0A0A] text-white rounded p-3 text-xs flex items-center justify-between flex-wrap gap-2"
                data-testid="opening-balances-equation">
                <span>Assets <span className="font-mono">${assetTotal.toFixed(2)}</span> = Liabilities <span className="font-mono">${liabilityTotal.toFixed(2)}</span> + Net Assets <span className="font-mono">${naAllocated.toFixed(2)}</span></span>
                {isBalanced && <span className="inline-flex items-center gap-1 text-green-300"><CheckCircle className="w-3 h-3" /> Balanced</span>}
                {!isBalanced && naAllocated > 0 && <span className="inline-flex items-center gap-1 text-amber-300"><AlertCircle className="w-3 h-3" /> Off by ${naDiff.toFixed(2)}</span>}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded" data-testid="opening-balances-error">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-[#E5E7EB] sticky bottom-0 bg-white">
          <button onClick={onClose} className="text-xs px-4 py-2 border border-[#E5E7EB] rounded text-[#4B5563] hover:bg-[#F8F9FA]">
            Cancel
          </button>
          <button onClick={save} disabled={saving || !isBalanced}
            className="text-xs px-4 py-2 bg-[#BA1826] text-white rounded font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="opening-balances-save">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save opening balances
          </button>
        </div>
      </div>
    </div>
  );
};

const COLOR = {
  blue: 'bg-blue-50 text-blue-800 border-blue-200',
  purple: 'bg-purple-50 text-purple-800 border-purple-200',
};

const BalanceList = ({ title, color, accounts, values, onChange, total, testidPrefix }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid={`${testidPrefix}-card`}>
    <div className="bg-[#FAFBFC] px-3 py-2 border-b border-[#E5E7EB] flex items-center justify-between">
      <p className="text-xs font-bold uppercase tracking-wider text-[#4B5563]">{title}</p>
      <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border ${COLOR[color]}`}>
        ${total.toFixed(2)}
      </span>
    </div>
    <div className="divide-y divide-[#E5E7EB]">
      {accounts.map((a) => (
        <div key={a.code} className="px-3 py-1.5 flex items-center gap-3">
          <span className="font-mono font-bold text-xs w-12">{a.code}</span>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-[#0A0A0A] truncate">{a.name}</p>
          </div>
          <input type="number" step="0.01" value={values[a.code] || ''}
            onChange={(e) => onChange(a.code, e.target.value)}
            placeholder="0.00"
            className="w-28 px-2 py-1 border border-[#E5E7EB] rounded text-xs text-right font-mono"
            data-testid={`${testidPrefix}-input-${a.code}`} />
        </div>
      ))}
    </div>
  </div>
);

export default OpeningBalancesWizard;
